import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnceWithDeps } from '@mafc/hooks';

import FEATURES_CONFIG from 'config/features';
import { IAppState } from 'config/redux/reducers';
import { isUndefined, isNonEmptyObject } from 'utils/helpers.util';

import { IAddress } from 'services/user/types/user.types';
import { IDeliverTo } from 'pages/Checkout/types/deliverTo.types';
import { deliverToPutAddress as deliverToPutAddressAction } from 'pages/Checkout/redux/actions/delivery.action';
import { showNotification } from 'common/Notification/redux/notification.action';
import { notificationTypes } from 'common/Notification/redux/notification.type';
import i18n from 'config/localisation';
import { goToCartHandler } from 'pages/Cart/helpers/url.helpers';
import { fetchPaymentArchitecture } from 'pages/Checkout/redux/actions/paymentArchitecture.action';
import useCartRevampEnabled from 'hooks/useCartRevampEnabled';

const useDeliverTo = (
  defaultAddress: IAddress,
  sessionAreaMatchedUserAddr: undefined | IAddress,
  setFallbackDeliveryAddrCallback?: () => void
): [
  IDeliverTo,
  {
    deliverToPutAddress: (callback?: () => void) => void;
  }
] => {
  const dispatch = useDispatch();
  const deliverToState = useSelector(({ checkout: { delivery } }: IAppState) => delivery.deliverTo);
  const isCartRevampEnabled = useCartRevampEnabled();
  const deliverToPutAddress = useCallback(
    (callback?: () => void) => {
      if (deliverToState.cache) {
        dispatch(deliverToPutAddressAction(deliverToState.cache, callback));
      }
    },
    [dispatch, deliverToState]
  );

  /**
   * CCX-524: Always set the the session area as the user's delivery area
   * Steps:
   * 1. Set the session areas as the delivery area (will skip setting of cookie since this value is already the cookie itself)
   * 2. Update cart by invoking Cart API again (will refresh `deliveryArea` value of the cart state)
   * 3. Set the slot for this address
   */
  useEffectOnceWithDeps(() => {
    // changeLocation=true: preselect address via Area Session
    if (FEATURES_CONFIG.settings.changeLocation) {
      if (!isUndefined(sessionAreaMatchedUserAddr)) {
        if (isNonEmptyObject(sessionAreaMatchedUserAddr)) {
          const fetchData = async () => {
            await dispatch(fetchPaymentArchitecture());
            dispatch(
              deliverToPutAddressAction(sessionAreaMatchedUserAddr as IAddress, undefined, false)
            );
          };
          fetchData();
        } else if (setFallbackDeliveryAddrCallback) {
          setFallbackDeliveryAddrCallback();
        } else {
          dispatch(
            showNotification({
              type: notificationTypes.errorCheckout,
              message: i18n.t('errorGeneral'),
            })
          );
          // discussed with Rohit
          setTimeout(() => {
            goToCartHandler(isCartRevampEnabled);
          }, 3000);
          // tslint:disable-next-line: no-console
          console.error('unable to find sessionAreaMatchedUserAddr', sessionAreaMatchedUserAddr);
        }

        return true;
      }
    } else {
      if (defaultAddress) {
        dispatch(deliverToPutAddressAction(defaultAddress));
        return true;
      }
    }
  }, [dispatch, defaultAddress, sessionAreaMatchedUserAddr, setFallbackDeliveryAddrCallback]);
  return [deliverToState, { deliverToPutAddress }];
};

export default useDeliverTo;
